import { CURRENCY_SIGNS, DISCOUNTS } from '@config/constants';
import {
  AttachmentFormat,
  Discount,
  DiscountValueType,
  EditionFormat,
  OrderItemDiscounts,
  PriceTypes,
  PricPrice,
  Product,
  Promotion,
  ViaxOrderItem,
  WileyBaseProduct,
  DiscountType,
  TrialInterval,
} from '@types';
import {
  constructProductUrl,
  SiteConfig,
} from '@utils';
import i18n from 'i18next';
import type { OrderInfo, PromoCode } from 'src/models/general';

type WileyProductVariant = ViaxOrderItem['biiSalable'];

interface OrderSummary {
  currency: string;
  orderInfo: OrderInfo;
}

export const getImgUrlByProduct = (baseProduct: WileyBaseProduct) => {
  return baseProduct.wImages.find((i) => i.wFormat === AttachmentFormat.NORMAL)?.atUrl || '';
};

const getProductFormat = (variant: WileyProductVariant): string => {
  const { isSubscriptionSite } = SiteConfig.getConfig();
  if (isSubscriptionSite) {
    return i18n.t('cart.productList.subscription');
  }

  return variant.maCategories[0]?.catName || '';
};

export const convertOrderItemsToProducts = (items: ViaxOrderItem[] = []): Product[] => {
  const { locale } = SiteConfig.getConfig();

  return items.map((item) => {
    const salable = item.biiSalable;
    const baseProduct = salable.parents?.[0]?.node;
    const title = salable?.maName || baseProduct?.maName || '';
    const url = constructProductUrl(locale, title, salable.maId);
    const image = getImgUrlByProduct(baseProduct);
    const total = item.pricPrice.edges.find(({ mapsOn }) => mapsOn.code === PriceTypes.SUBTOTAL)?.node;
    const currencyCode = total?.units.code || 'USD';
    const currency = CURRENCY_SIGNS[currencyCode] || '$';
    const qty = item.biiQuantity?.amount ?? 1;
    const price = item.pricPrice.edges.find(({ mapsOn }) => mapsOn.code === PriceTypes.PRICE)?.node.amount || 0;
    const discountAmount = +Number(qty * price).toFixed(2);

    return {
      url,
      title,
      uid: item.uid,
      biiId: item.biiId,
      maId: salable.maId,
      image,
      qty,
      salableUid: salable.uid,
      format: getProductFormat(salable),
      digitalProduct: salable?.wEditionFormat === EditionFormat.DIGITAL,
      physicalProduct: salable?.wEditionFormat === EditionFormat.PHYSICAL,
      currency,
      discountAmount,
      amount: total?.amount,
      parentId: salable.parentId,
      categoryName: salable.categoryName,
      status: salable.status,
      printOnDemand: salable.wPrintOnDemand,
      licenseAgreementInfo: salable.wProductLicenseAgreementInfo,
      // eslint-disable-next-line no-underscore-dangle
      typename: item.__typename,

      // TODO: check later, if fields are not used - remove everywhere
      // courseId: '',
      // instructors: '',
      // brand: ''
    };
  });
};

export const getCurrency = (pricPrice: PricPrice): string => {
  const total = pricPrice.edges.find(({ mapsOn }) => mapsOn.code === PriceTypes.SUBTOTAL)?.node;
  const currencyCode = total?.units.code || 'USD';
  const currency = CURRENCY_SIGNS[currencyCode] || '$';
  return currency;
};

export const convertOrderSummary = (pricPrice: PricPrice, products: Product[]): OrderSummary => {
  const total = pricPrice.edges.find(({ mapsOn }) => mapsOn.code === PriceTypes.SUBTOTAL)?.node;
  const productTotal = products.reduce((acc, product) => acc + (product.discountAmount || 0), 0);
  const currency = getCurrency(pricPrice);

  return {
    currency,
    orderInfo: { orderTotal: total?.amount, productTotal },
  };
};

export const convertPromoCode = (
  corPromoCodes: Promotion[],
  pricPrice: PricPrice,
  amount: number,
): PromoCode | null => {
  if (!corPromoCodes?.length) return null;
  const currency = getCurrency(pricPrice);
  const corPromoCodesIndexZero = corPromoCodes[0];

  return {
    discount: { amount, currency },
    value: corPromoCodesIndexZero.prmId,
    freeShipping: corPromoCodesIndexZero.wFreeStandardShipping,
  };
};

const getDiscount = (discount: OrderItemDiscounts, currency: string): string => {
  const isFreeTrial = discount.discountType === DiscountType.FREETRIAL;

  if (isFreeTrial) {
    const amount = discount.freeTrialDuration?.amount || 0;
    const units = discount.freeTrialDuration?.units || TrialInterval.DAY;
    const interval = units.toLowerCase();
    const dayText = i18n.t(`cart.common.${interval}`);
    return `${amount}-${dayText}`;
  }

  const isAmount = discount.valueType === DiscountValueType.AMOUNT;
  const discountSign = isAmount ? currency : '%';
  const discountValue = isAmount
    ? `${discountSign}${discount.discountValue}`
    : `${discount.discountValue}${discountSign}`;
  return `${i18n.t('cart.productList.save')} ${discountValue}`;
};

export const convertDiscounts = (
  discounts: OrderItemDiscounts[],
  promoCode: PromoCode | null,
  pricPrice: PricPrice,
): Discount[] => {
  const currency = getCurrency(pricPrice);
  const coupon = promoCode ? promoCode.value : '';
  return discounts.map((item) => {
    const discount = getDiscount(item, currency);
    const title = DISCOUNTS[item.discountType] || coupon;

    return {
      freeTrialDuration: item.freeTrialDuration,
      itemNumber: item.itemNumber,
      discount,
      title,
    };
  });
};
